import {
  afterNextRender,
  booleanAttribute,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  output,
} from '@angular/core';
import { EventqShortcutService } from './shortcut.service';

const IGNORE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'OPTION']);

function isActivateKey(event: KeyboardEvent): boolean {
  return event.key === ' ' || event.key === 'Enter' || event.key === 'Spacebar' || event.key === 'Space';
}

/**
 * Skip shortcuts when the focused control needs the key (typing, activating a
 * button). Arrow keys still pass through buttons/links so page-level navigation
 * keeps working when focus sits on a toolbar control.
 */
function shouldIgnoreTarget(target: EventTarget | null, event: KeyboardEvent): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) {
    return true;
  }
  if (IGNORE_TAGS.has(target.tagName)) {
    return true;
  }
  if (target instanceof HTMLInputElement && target.type === 'range') {
    return true;
  }
  if (target.tagName === 'BUTTON' || (target.tagName === 'A' && target.hasAttribute('href'))) {
    return isActivateKey(event);
  }
  return false;
}

/**
 * Shortcut listener for a host region. Default: only while focus is inside the
 * host. With `eventqShortcutDocument`, listens on `document` so keys work for
 * the whole page while this host is mounted (still ignores typing targets).
 */
@Directive({
  selector: '[eventqShortcutScope]',
  host: {
    '(keydown)': 'onHostKeydown($event)',
  },
})
export class EventqShortcutScope {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly shortcuts = inject(EventqShortcutService);
  private readonly destroyRef = inject(DestroyRef);

  /** When set, only these action ids are emitted from this scope. */
  readonly eventqShortcutScope = input<readonly string[] | ''>('');

  /**
   * When true, listen on `document` so shortcuts work without focus inside the
   * host (e.g. Event Details page-level alarm keys).
   */
  readonly eventqShortcutDocument = input(false, { transform: booleanAttribute });

  readonly eventqShortcut = output<string>();

  private readonly lastFiredAt = new Map<string, number>();

  constructor() {
    afterNextRender(() => {
      const onDocumentKeydown = (event: KeyboardEvent): void => {
        if (!this.eventqShortcutDocument()) {
          return;
        }
        this.handleKeydown(event);
      };
      document.addEventListener('keydown', onDocumentKeydown);
      this.destroyRef.onDestroy(() => {
        document.removeEventListener('keydown', onDocumentKeydown);
      });
    });
  }

  onHostKeydown(event: KeyboardEvent): void {
    if (this.eventqShortcutDocument()) {
      // Document listener owns page-level scopes; avoid double-firing.
      return;
    }
    const active = document.activeElement;
    const focusedInside =
      active instanceof Node && this.host.nativeElement.contains(active);
    if (!focusedInside) {
      return;
    }
    this.handleKeydown(event);
  }

  private handleKeydown(event: KeyboardEvent): void {
    const active = document.activeElement;
    if (shouldIgnoreTarget(active, event)) {
      return;
    }

    const action = this.shortcuts.resolve(event);
    if (!action) {
      return;
    }

    const allowed = this.eventqShortcutScope();
    if (Array.isArray(allowed) && allowed.length > 0 && !allowed.includes(action.id)) {
      return;
    }

    if (event.repeat && !action.allowRepeat) {
      event.preventDefault();
      return;
    }

    const throttleMs = action.throttleMs ?? 0;
    if (throttleMs > 0) {
      const now = Date.now();
      const last = this.lastFiredAt.get(action.id) ?? 0;
      if (now - last < throttleMs) {
        event.preventDefault();
        return;
      }
      this.lastFiredAt.set(action.id, now);
    }

    event.preventDefault();
    this.eventqShortcut.emit(action.id);
  }
}
